/**
 * Number / unit / time formatters. Tabular-friendly, locale-stable, and
 * sign-aware. Sign convention is canonical:
 *   grid +ve = import, BESS +ve = charging, controller +ve = charge command.
 */

/**
 * Canonical power noise floor (watts). Below this magnitude a power
 * reading is treated as "idle" — sensor noise, inverter standby draw,
 * and metering jitter routinely sit in the 50–400 W band on a real
 * BESS site, so anything under this should NOT render as activity
 * (no animated flow, no charge/discharge colour, no full distribution
 * bar). 500 W ≈ 0.5 % of a 100 kW inverter — comfortably noise.
 *
 * This is the single source of truth: `direction`, `bessDirection`,
 * the SCADA flow diagram, the BESS tiles/cabinets, the RightNow strip,
 * and the per-BESS distribution bar all key off this. Promote to
 * per-site config only if a deployment genuinely needs a different
 * floor — until then one constant keeps every surface consistent.
 */
export const IDLE_DEADBAND_W = 500;

/** True when a power reading is within the idle noise floor. */
export function isIdlePower(watts: number): boolean {
  return Math.abs(watts) < IDLE_DEADBAND_W;
}

/**
 * Single locale switch point (DSGN-11). Every Intl formatter in the app —
 * here and in the page-level outliers (MarketPricePage, bessEnergyBuckets) —
 * must consume this constant, so the whole SPA changes decimal/date style
 * in one place. en-GB keeps the established style: "1,234.56" grouping and
 * "12 Jun 2026" dates (identical numerics to the previous en-US/en-IE pins,
 * so nothing visibly changes from unifying).
 */
export const LOCALE = "en-GB";

const fmt = (digits: number) =>
  new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: true,
  });
const f0 = fmt(0);
const f1 = fmt(1);
const f2 = fmt(2);

function pickFmt(digits: number) {
  return digits === 0 ? f0 : digits === 1 ? f1 : f2;
}

export function signedKW(watts: number, digits = 1): string {
  const kw = watts / 1000;
  const f = pickFmt(digits);
  if (Math.abs(kw) < 0.05 && digits >= 1) return "0";
  return (kw > 0 ? "+" : kw < 0 ? "−" : "") + f.format(Math.abs(kw));
}

export function unsignedKW(watts: number, digits = 1) {
  const f = pickFmt(digits);
  return f.format(Math.abs(watts) / 1000);
}

export function formatKWh(kWh: number, digits = 0) {
  const f = pickFmt(digits);
  return f.format(kWh);
}

/** Equivalent full cycles — 1 cycle = charging AND discharging the full nominal capacity once. */
export function cyclesFromThroughput(throughputKwh: number, capacityKwh: number): number {
  return capacityKwh > 0 ? throughputKwh / (2 * capacityKwh) : 0;
}

export function formatPercent(pct: number, digits = 0) {
  return `${pickFmt(digits).format(pct)}%`;
}

export function formatHz(hz: number) { return `${f2.format(hz)} Hz`; }
export function formatV(v: number) { return `${f1.format(v)} V`; }

export function formatEur(eur: number, digits = 2) {
  return new Intl.NumberFormat(LOCALE, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(eur);
}

export function formatRelativeAge(ageMs: number): string {
  if (ageMs < 0) return formatRelativeFuture(-ageMs);
  const s = Math.round(ageMs / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return `${d} d ago`;
}

export function formatRelativeFuture(deltaMs: number): string {
  const s = Math.round(deltaMs / 1000);
  if (s < 5) return "imminent";
  if (s < 60) return `in ${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `in ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `in ${h} h`;
  const d = Math.round(h / 24);
  return `in ${d} d`;
}

export function formatTimeOfDay(ts: number) {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
}

export function formatHourMin(ts: number) {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function formatDate(ts: number) {
  return new Date(ts).toLocaleDateString(LOCALE, {
    day: "2-digit", month: "short", year: "numeric",
  });
}

export function formatDateTime(ts: number) {
  return `${formatDate(ts)} · ${formatHourMin(ts)}`;
}

export function direction(watts: number): "import" | "export" | "idle" {
  if (isIdlePower(watts)) return "idle";
  return watts > 0 ? "import" : "export";
}

export function bessDirection(watts: number): "charge" | "discharge" | "idle" {
  if (isIdlePower(watts)) return "idle";
  return watts > 0 ? "charge" : "discharge";
}
